import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import Button from "@mui/material/Button";
import FancyPaper from "../components/FancyPaper";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../hooks/useApp";
import {
  AguinaldoDetail,
  AguinaldoEntrySavePayload,
  AguinaldoMonthly,
  getAguinaldoDetail,
  saveAguinaldoEntry,
} from "../services/aguinaldos/AguinaldosService";

// Intl.NumberFormat("es-CR", ...) agrupa los miles con un espacio, no con
// punto — no hay locale de Intl que dé "punto de miles, coma decimal" y
// símbolo ₡ de forma confiable a la vez. Se arma el número con un locale que
// sí agrupa con punto (de-DE) y se antepone el símbolo a mano.
const formatCRC = (value: number) =>
  `₡${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

const buildYearOptions = () => {
  const current = new Date().getFullYear();
  return [current, current - 1, current - 2, current - 3];
};

interface SummaryCardProps {
  title: string;
  value: number;
  highlight?: boolean;
  color?: string;
}

const SummaryCard = ({ title, value, highlight, color }: SummaryCardProps) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      borderColor: highlight ? "primary.main" : undefined,
      bgcolor: highlight ? "primary.50" : undefined,
    }}
  >
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {title}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        color={color ?? (highlight ? "primary.main" : "text.primary")}
        noWrap
      >
        {formatCRC(value)}
      </Typography>
    </CardContent>
  </Card>
);

interface EntryForm {
  maternidadPaternidad: string;
  globalDeductions: string;
}

const DetailAguinaldos = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { personId, year: yearParam } = useParams<{ personId: string; year: string }>();
  const { authInfo, setErrorMsg, setSuccessMsg } = useApp();

  const yearOptions = useMemo(() => buildYearOptions(), []);
  const [selectedYear, setSelectedYear] = useState(
    yearParam ? Number(yearParam) : yearOptions[0]
  );
  const [detail, setDetail] = useState<AguinaldoDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingMonth, setEditingMonth] = useState<AguinaldoMonthly | null>(null);
  const [entryForm, setEntryForm] = useState<EntryForm>({
    maternidadPaternidad: "0",
    globalDeductions: "0",
  });
  const [saving, setSaving] = useState(false);

  const loadDetail = useCallback(async () => {
    if (!personId) return;
    setLoading(true);
    try {
      const data = await getAguinaldoDetail(Number(personId), selectedYear);
      setDetail(data);
    } catch (e: any) {
      setErrorMsg && setErrorMsg(e.message ?? "Error al cargar el detalle");
    } finally {
      setLoading(false);
    }
  }, [personId, selectedYear, setErrorMsg]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const openEdit = (month: AguinaldoMonthly) => {
    setEditingMonth(month);
    setEntryForm({
      maternidadPaternidad: String(month.maternidadPaternidad),
      globalDeductions: String(month.globalDeductions),
    });
  };

  const handleSaveEntry = async () => {
    if (!editingMonth || !personId) return;
    setSaving(true);
    try {
      const payload: AguinaldoEntrySavePayload = {
        personId: Number(personId),
        year: selectedYear,
        month: editingMonth.month,
        // Esta pantalla no edita el salario ordinario: se reenvía tal cual para no
        // borrar el monto manual cargado desde la pantalla de Aguinaldos.
        salarioOrdinarioManual: editingMonth.salarioOrdinarioManual,
        maternidadPaternidad: Number(entryForm.maternidadPaternidad) || 0,
        globalDeductions: Number(entryForm.globalDeductions) || 0,
        pensionQuantity: 0,
        pensionUnitAmount: 0,
        user: authInfo?.username ?? "",
      };
      await saveAguinaldoEntry(payload);
      setSuccessMsg && setSuccessMsg("Entrada de aguinaldo guardada correctamente");
      setEditingMonth(null);
      await loadDetail();
    } catch (e: any) {
      setErrorMsg && setErrorMsg(e.message ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FancyPaper
      pagetitle="Detalle de Aguinaldo"
      prevpage="Aguinaldos"
      linkprevpage="/dashboard/aguinaldos"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, flexWrap: "wrap" }}>
        <IconButton onClick={() => navigate("/dashboard/aguinaldos")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          {detail?.fullName ?? "Cargando…"}
        </Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary">
            {detail.positionName}
          </Typography>
        )}
        <Box sx={{ ml: "auto" }}>
          <TextField
            select
            size="small"
            label="Año aguinaldo"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ width: 140 }}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {detail && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          Período: {detail.periodStart} — {detail.periodEnd}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : detail ? (
        <>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard title="Salario base" value={detail.baseSalary} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard title="Total ordinario" value={detail.totalOrdinary} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard title="Total maternidad" value={detail.totalMaternidad} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard title="Gran total" value={detail.grandTotal} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard title="Aguinaldo bruto" value={detail.aguinaldoBruto} highlight />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard
                title="Neto a pagar"
                value={detail.netToPay}
                color="success.main"
                highlight
              />
            </Grid>
          </Grid>

          {/* Deducciones globales */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: "error.50",
              border: "1px solid",
              borderColor: "error.light",
            }}
          >
            <Typography variant="body2" color="error.dark" fontWeight={600}>
              Deducciones globales:
            </Typography>
            <Typography variant="body2" color="error.dark">
              {formatCRC(detail.globalDeductions)}
            </Typography>
          </Box>

          {/* Monthly table */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Tabla mensual (Dic {selectedYear - 1} — Nov {selectedYear})
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                  {["Mes", "Salario ordinario", "Maternidad / Paternidad", "Total mensual", ""].map(
                    (h, i) => (
                      <TableCell key={i} sx={{ color: "#fff", fontWeight: 600 }} align={i > 0 && i < 4 ? "right" : "left"}>
                        {h}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.months.map((m, idx) => (
                  <TableRow
                    key={`${m.year}-${m.month}`}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? "transparent" : theme.palette.action.hover,
                    }}
                  >
                    <TableCell>{m.monthLabel}</TableCell>
                    <TableCell align="right">{formatCRC(m.salarioOrdinario)}</TableCell>
                    <TableCell align="right">{formatCRC(m.maternidadPaternidad)}</TableCell>
                    <TableCell align="right">
                      <strong>{formatCRC(m.totalMensual)}</strong>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar entrada manual">
                        <IconButton size="small" color="primary" onClick={() => openEdit(m)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                  <TableCell>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCRC(detail.totalOrdinary)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCRC(detail.totalMaternidad)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{formatCRC(detail.grandTotal)}</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Aguinaldo summary */}
          <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50], border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Resumen del aguinaldo
            </Typography>
            {[
              { label: "Gran total / 12", value: formatCRC(detail.grandTotal) + " / 12" },
              { label: "Aguinaldo bruto", value: formatCRC(detail.aguinaldoBruto) },
              { label: "Deducciones globales", value: `- ${formatCRC(detail.globalDeductions)}` },
            ].map((item) => (
              <Box key={item.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                <Typography variant="body2">{item.value}</Typography>
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1,
                px: 1,
                bgcolor: "success.light",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700}>Neto a pagar</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="success.dark">
                {formatCRC(detail.netToPay)}
              </Typography>
            </Box>
          </Box>
        </>
      ) : null}

      {/* Edit entry dialog */}
      <Dialog open={editingMonth !== null} onClose={() => setEditingMonth(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Editar entrada — {editingMonth?.monthLabel}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Maternidad / Paternidad"
              size="small"
              type="number"
              value={entryForm.maternidadPaternidad}
              onChange={(e) =>
                setEntryForm((f) => ({ ...f, maternidadPaternidad: e.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
              helperText="Monto adicional por maternidad o paternidad"
            />
            <TextField
              label="Deducciones globales"
              size="small"
              type="number"
              value={entryForm.globalDeductions}
              onChange={(e) =>
                setEntryForm((f) => ({ ...f, globalDeductions: e.target.value }))
              }
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
              helperText="Embargos, pensiones alimentarias u otras deducciones"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMonth(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveEntry} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </FancyPaper>
  );
};

export default DetailAguinaldos;
